import { DeviceType, Group } from "@fairfleet/geotab";
import { CustomProperty } from "@/models/CustomProperty";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";

function trimOrUndefined(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function splitAssetTypeGroup(
  groups: Group[],
  assetTypeGroups: Group[],
): { assetTypeGroup: Group | null; otherGroups: Group[] } {
  const assetTypeIds = new Set(assetTypeGroups.map(g => g.id));
  const index = groups.findIndex(g => assetTypeIds.has(g.id));
  if (index === -1) {
    return { assetTypeGroup: null, otherGroups: groups };
  }
  return {
    assetTypeGroup: groups[index],
    otherGroups: [...groups.slice(0, index), ...groups.slice(index + 1)],
  };
}


export function buildSerialNumberEntity(
  device: DeviceWithVehicleInfo,
  nextSerialNumber: string | undefined,
): DeviceWithVehicleInfo {
  return { ...device, serialNumber: nextSerialNumber ?? "" };
}

export function buildUpdatedDeviceEntity(
  base: DeviceWithVehicleInfo,
  form: {
    name: string;
    comment: string;
    licensePlate: string;
    vehicleIdentificationNumber: string;
    serialNumber: string;
    customProperties: CustomProperty[];
    groups: Group[];
  },
): DeviceWithVehicleInfo {
  return {
    ...base,
    name: form.name.trim(),
    comment: trimOrUndefined(form.comment) ?? "",
    licensePlate: trimOrUndefined(form.licensePlate),
    vehicleIdentificationNumber: trimOrUndefined(form.vehicleIdentificationNumber),
    serialNumber: trimOrUndefined(form.serialNumber) ?? "",
    customProperties: form.customProperties,
    groups: form.groups.map(group => ({ id: group.id } as Group)),
  } as unknown as DeviceWithVehicleInfo;
}

export function shouldHydrateForm({
  isCreatingNewAsset,
  lastKey,
  nextKey,
}: {
  isCreatingNewAsset: boolean;
  lastKey: string;
  nextKey: string;
}) {
  return !isCreatingNewAsset && lastKey !== nextKey;
}

export type AssetFormValues = {
  name: string;
  comment: string;
  licensePlate: string;
  vehicleIdentificationNumber: string;
  serialNumber: string;
  customProperties: CustomProperty[];
  groups: Group[];
};

export function buildNewDeviceEntity(form: AssetFormValues): DeviceWithVehicleInfo {
  return {
    name: form.name.trim(),
    deviceType: "CustomVehicleDevice" as DeviceType,
    comment: trimOrUndefined(form.comment) ?? "",
    licensePlate: trimOrUndefined(form.licensePlate),
    vehicleIdentificationNumber: trimOrUndefined(form.vehicleIdentificationNumber),
    serialNumber: trimOrUndefined(form.serialNumber) ?? "",
    customProperties: form.customProperties,
    groups: form.groups.map(group => ({ id: group.id } as Group)),
  } as unknown as DeviceWithVehicleInfo;
}

export function hasDeviceChanges(original: DeviceWithVehicleInfo | null, form: AssetFormValues): boolean {
  if (!original) {
    return (
      form.name.trim().length > 0 ||
      form.comment.trim().length > 0 ||
      form.licensePlate.trim().length > 0 ||
      form.vehicleIdentificationNumber.trim().length > 0 ||
      form.serialNumber.trim().length > 0 ||
      form.customProperties.length > 0 ||
      form.groups.length > 0
    );
  }

  const originalRecord = original as unknown as Record<string, unknown>;
  const originalCustomProperties = (original.customProperties as CustomProperty[]) ?? [];
  const originalGroups = (original.groups as Group[] | undefined) ?? [];

  const hasCustomPropertyChanges = form.customProperties.some(customProperty => {
    const originalProperty = originalCustomProperties.find(item => item.property?.id === customProperty.property?.id);
    return (originalProperty?.value ?? "") !== (customProperty.value ?? "");
  });
  const hasRemovedCustomProperty = originalCustomProperties.length !== form.customProperties.length;

  const selectedGroupIds = form.groups.map(group => group.id).sort();
  const originalGroupIds = originalGroups.map(group => group.id).sort();
  const hasGroupChanges =
    selectedGroupIds.length !== originalGroupIds.length ||
    selectedGroupIds.some((groupId, index) => groupId !== originalGroupIds[index]);

  return (
    (original.name ?? "") !== form.name ||
    (original.comment ?? "") !== form.comment ||
    String(originalRecord.licensePlate ?? "") !== form.licensePlate ||
    String(originalRecord.vehicleIdentificationNumber ?? "") !== form.vehicleIdentificationNumber ||
    (original.serialNumber ?? "") !== form.serialNumber ||
    hasCustomPropertyChanges ||
    hasRemovedCustomProperty ||
    hasGroupChanges
  );
}

/** Everything the editor form holds, with the asset type group kept apart from the other groups. */
export type AssetFormState = Omit<AssetFormValues, "groups"> & {
  assetTypeGroup: Group | null;
  groups: Group[];
};

export const EMPTY_ASSET_FORM: AssetFormState = {
  name: "",
  comment: "",
  licensePlate: "",
  vehicleIdentificationNumber: "",
  serialNumber: "",
  customProperties: [],
  assetTypeGroup: null,
  groups: [],
};

export function toAssetFormValues(form: AssetFormState): AssetFormValues {
  const { assetTypeGroup, groups, ...rest } = form;
  return { ...rest, groups: [assetTypeGroup, ...groups].filter((group): group is Group => !!group) };
}

/**
 * The form values a device hydrates the editor with, plus a key that changes whenever any of them
 * does, so the editor can tell a real change from a re-render.
 */
export function buildAssetFormFromDevice(
  device: DeviceWithVehicleInfo | null,
  allGroups: Group[],
  assetTypeGroups: Group[],
): { key: string; form: AssetFormState } {
  const record = device as Record<string, unknown> | null;
  const customProperties = ((device?.customProperties as CustomProperty[]) ?? []).map(property => ({
    ...property,
  }));
  const deviceGroups = ((device?.groups as Group[] | undefined) ?? []).map(group => {
    const fullGroup = allGroups.find(candidate => candidate.id === group.id);
    return fullGroup ?? group;
  });
  const { assetTypeGroup, otherGroups } = splitAssetTypeGroup(deviceGroups, assetTypeGroups);

  const form: AssetFormState = {
    name: device?.name ?? "",
    comment: device?.comment ?? "",
    licensePlate: String(record?.licensePlate ?? ""),
    vehicleIdentificationNumber: String(record?.vehicleIdentificationNumber ?? ""),
    serialNumber: device?.serialNumber ?? "",
    customProperties,
    assetTypeGroup,
    groups: otherGroups,
  };

  const key = JSON.stringify({
    id: device?.id ?? "",
    name: form.name,
    comment: form.comment,
    licensePlate: form.licensePlate,
    vin: form.vehicleIdentificationNumber,
    serialNumber: form.serialNumber,
    customProperties: customProperties.map(item => ({ id: item.property?.id, value: item.value })),
    groups: deviceGroups.map(group => group.id),
    assetTypeGroup: assetTypeGroup?.id ?? null,
  });

  return { key, form };
}
